import { createFileRoute } from '@tanstack/react-router';
import { purgeResilientFetchQuery } from '@/lib/query-invalidation/purge-resilient-fetch';
import { cacheFirst } from '@/lib/queries/cache-first';
import { getHomeFeatureRequests, getHomeUpcomingClasses } from '@/lib/server-fns/homepage-fns';
import { HomeRoutePending } from './-home-pending';
import { createHomeLoaderData } from './-home-loader-data';

// /v2/homepage sections are globally identical for every learner, and the
// documented freshness policy is 5 minutes (the retired SSR pilot used a 300s
// max-age Cache-Control).
// This route has no mounted Query observer, so the loader is the only freshness
// gate: a cold cache (SSR first load) streams the loader fetch; a cached
// section resolves the loader INSTANTLY with the cached value while the plane
// refreshes it in the background once past the 5-minute staleTime (the refresh
// is a fetchQuery, so the heal really refetches). Both sections are loaded as
// independent promises and rendered behind separate Await boundaries, so a slow
// or failed section degrades to its own local error instead of holding back the
// page chrome or the other section.
const HOME_SECTION_STALE_TIME = 5 * 60 * 1000;
const HOME_FEATURE_REQUESTS_KEY = ['home', 'feature-requests'];
const HOME_UPCOMING_CLASSES_KEY = ['home', 'upcoming-classes'];

export const Route = createFileRoute('/_authenticated/home/')({
  // Template-first pending while the home chunk downloads (first visit):
  // the page's real container/section rhythm with skeleton blocks.
  pendingComponent: HomeRoutePending,
  loader: ({ context }) => {
    const loadSection = <T,>(
      queryKey: readonly unknown[],
      queryFn: () => Promise<T[]>
    ): Promise<T[]> => {
      // purgeResilient keeps the section Retry → router.invalidate() path live
      // across the login identity purge; a background refresh failure is
      // swallowed (the stale cache stays on screen, next navigation retries).
      const refresh = () =>
        purgeResilientFetchQuery(context.queryClient, {
          queryKey,
          queryFn,
          staleTime: HOME_SECTION_STALE_TIME,
        });
      // Cache-first: no mounted observer owns freshness on this route, so the
      // loader's own refresh closure is the heal the plane fires for a stale
      // entry; a cold cache streams the same fetch its Await boundary renders
      // against.
      const { cached, stream } = cacheFirst(context.queryClient, queryKey, refresh, {
        staleTime: HOME_SECTION_STALE_TIME,
      });
      // The entry was written by this loader's own queryFn, so the cached
      // value carries the section's item type.
      return cached !== undefined ? Promise.resolve(cached) : stream();
    };

    return createHomeLoaderData(
      () => loadSection(HOME_FEATURE_REQUESTS_KEY, getHomeFeatureRequests),
      () => loadSection(HOME_UPCOMING_CLASSES_KEY, getHomeUpcomingClasses)
    );
  },
});
